import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';

export interface TimelinePositionEngine {
  get(): number;
  getTarget(): number;
  setTarget(next: number): void;
  scrollBy(delta: number): void;
  /** Kalles hver frame mens motoren animerer, og en gang ved hvert sett-kall. Returnerer avmelding. */
  subscribe(listener: (position: number) => void): () => void;
}

interface Bounds {
  readonly min: number;
  readonly max: number;
  readonly initial?: number;
}

const STOP_EPSILON = 0.0005;
const BASE_ALPHA = 0.14;

export function useTimelinePosition(bounds: Bounds): TimelinePositionEngine {
  const normalized = normalizeBounds(bounds.min, bounds.max);
  const minRef = useRef(normalized.min);
  const maxRef = useRef(normalized.max);
  const positionRef = useRef(
    clamp(bounds.initial ?? normalized.min, normalized.min, normalized.max),
  );
  const targetRef = useRef(positionRef.current);
  const listenersRef = useRef(new Set<(position: number) => void>());
  const reducedMotionRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const initialAppliedRef = useRef(false);
  const notify = useNotify(listenersRef);
  const stopAnimation = useStopAnimation(rafRef, lastTsRef);
  const ensureAnimation = useEnsureAnimation({
    positionRef,
    targetRef,
    notify,
    stopAnimation,
    rafRef,
    lastTsRef,
  });

  useReducedMotion(reducedMotionRef);
  useSyncBounds(bounds, initialAppliedRef, minRef, maxRef, positionRef, targetRef, notify);
  useEffect(() => stopAnimation, [stopAnimation]);

  const setTarget = useCallback(
    (next: number) => {
      const clamped = clamp(next, minRef.current, maxRef.current);
      targetRef.current = clamped;
      notify(positionRef.current);
      const shouldSnap =
        reducedMotionRef.current || Math.abs(clamped - positionRef.current) < STOP_EPSILON;
      if (shouldSnap) {
        positionRef.current = clamped;
        notify(clamped);
        stopAnimation();
        return;
      }
      ensureAnimation();
    },
    [ensureAnimation, notify, stopAnimation],
  );

  const scrollBy = useCallback(
    (delta: number) => setTarget(targetRef.current + delta),
    [setTarget],
  );
  const get = useCallback(() => positionRef.current, []);
  const getTarget = useCallback(() => targetRef.current, []);
  const subscribe = useCallback((listener: (position: number) => void) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  return useMemo(
    () => ({ get, getTarget, setTarget, scrollBy, subscribe }),
    [get, getTarget, scrollBy, setTarget, subscribe],
  );
}

function useNotify(listenersRef: MutableRefObject<Set<(position: number) => void>>) {
  return useCallback(
    (position: number) => {
      for (const listener of listenersRef.current) listener(position);
    },
    [listenersRef],
  );
}

function useStopAnimation(
  rafRef: MutableRefObject<number | null>,
  lastTsRef: MutableRefObject<number | null>,
) {
  return useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    lastTsRef.current = null;
  }, [lastTsRef, rafRef]);
}

interface EnsureAnimationArgs {
  readonly positionRef: MutableRefObject<number>;
  readonly targetRef: MutableRefObject<number>;
  readonly notify: (position: number) => void;
  readonly stopAnimation: () => void;
  readonly rafRef: MutableRefObject<number | null>;
  readonly lastTsRef: MutableRefObject<number | null>;
}

function useEnsureAnimation({
  positionRef,
  targetRef,
  notify,
  stopAnimation,
  rafRef,
  lastTsRef,
}: EnsureAnimationArgs) {
  const animateStep = useCallback(
    (timestamp: number) => {
      const current = positionRef.current;
      const target = targetRef.current;
      const distance = target - current;
      if (Math.abs(distance) < STOP_EPSILON) {
        positionRef.current = target;
        notify(target);
        stopAnimation();
        return;
      }
      const previousTs = lastTsRef.current ?? timestamp;
      const deltaMs = Math.max(1, Math.min(64, timestamp - previousTs));
      lastTsRef.current = timestamp;
      const frameFactor = deltaMs / (1000 / 60);
      const alpha = 1 - Math.pow(1 - BASE_ALPHA, frameFactor);
      positionRef.current = current + distance * alpha;
      notify(positionRef.current);
      rafRef.current = requestAnimationFrame(animateStep);
    },
    [lastTsRef, notify, positionRef, rafRef, stopAnimation, targetRef],
  );

  return useCallback(() => {
    if (rafRef.current !== null) return;
    lastTsRef.current = null;
    rafRef.current = requestAnimationFrame(animateStep);
  }, [animateStep, lastTsRef, rafRef]);
}

function useReducedMotion(reducedMotionRef: MutableRefObject<boolean>): void {
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      reducedMotionRef.current = query.matches;
    };
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [reducedMotionRef]);
}

function useSyncBounds(
  bounds: Bounds,
  initialAppliedRef: MutableRefObject<boolean>,
  minRef: MutableRefObject<number>,
  maxRef: MutableRefObject<number>,
  positionRef: MutableRefObject<number>,
  targetRef: MutableRefObject<number>,
  notify: (position: number) => void,
): void {
  useEffect(() => {
    const normalized = normalizeBounds(bounds.min, bounds.max);
    minRef.current = normalized.min;
    maxRef.current = normalized.max;
    if (!initialAppliedRef.current && bounds.initial !== undefined) {
      initialAppliedRef.current = true;
      const start = clamp(bounds.initial, normalized.min, normalized.max);
      positionRef.current = start;
      targetRef.current = start;
      notify(start);
      return;
    }
    const nextPosition = clamp(positionRef.current, normalized.min, normalized.max);
    const nextTarget = clamp(targetRef.current, normalized.min, normalized.max);
    const changed = nextPosition !== positionRef.current || nextTarget !== targetRef.current;
    positionRef.current = nextPosition;
    targetRef.current = nextTarget;
    if (changed) notify(nextPosition);
  }, [
    bounds.initial,
    bounds.max,
    bounds.min,
    initialAppliedRef,
    maxRef,
    minRef,
    notify,
    positionRef,
    targetRef,
  ]);
}

function normalizeBounds(min: number, max: number): { min: number; max: number } {
  return { min: Math.min(min, max), max: Math.max(min, max) };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
